/**
 * 皮肤：设计 token 的**唯一来源**。
 *
 * `dist/tokens.css` 是从这个文件生成的（`scripts/build-css.mjs`），不要手改那个文件。
 *
 * 为什么要从 CSS 挪到 TS：
 *
 * 1. **深色值曾经存在两份手抄副本**——一份在 `@media (prefers-color-scheme: dark)` 里，
 *    一份在 `:root[data-theme="dark"]` 里（切主题开关要盖过系统偏好，两个选择器都得有）。
 *    37 个值抄两遍，改一边漏一边而且完全静默：表现是"跟随系统时颜色对，手动切深色就有一处不对"。
 *    这正是这个框架反复在消灭的那类 bug，不该留在自己的样式层里。
 * 2. **定制皮肤时需要知道有哪些槽位**。散在 255 行 CSS 里的变量名不是契约，
 *    一个 TS 接口才是——写错名字当场报错，而不是"某个颜色莫名其妙没生效"。
 *
 * 换品牌色通常只要改 `accent` 那一组：见 `defineSkin()`。
 */
/* ─────────────────────── token 名 → CSS 变量名 ───────────────────────
 * 显式写死，不用自动 kebab 转换。
 * 自动转换会在 `surface2 → --ps-surface2`、`tInstant → --ps-t-instant` 这类地方翻车，
 * 而翻车的表现是"某个变量静默失效、组件退回浏览器默认值"，非常难查。
 * 这张表由单测锁住：生成的变量名集合必须和组件 CSS 里用到的完全对上。
 */
export const PALETTE_VARS = {
    canvas: '--ps-canvas',
    surface: '--ps-surface',
    surface2: '--ps-surface-2',
    surfaceInput: '--ps-surface-input',
    surfaceSunken: '--ps-surface-sunken',
    overlay: '--ps-overlay',
    line: '--ps-line',
    lineSoft: '--ps-line-soft',
    lineStrong: '--ps-line-strong',
    text: '--ps-text',
    text2: '--ps-text-2',
    text3: '--ps-text-3',
    text4: '--ps-text-4',
    textInverse: '--ps-text-inverse',
    accent: '--ps-accent',
    accentHover: '--ps-accent-hover',
    accentActive: '--ps-accent-active',
    accentSoft: '--ps-accent-soft',
    accentLine: '--ps-accent-line',
    onAccent: '--ps-on-accent',
    success: '--ps-success',
    successSoft: '--ps-success-soft',
    warn: '--ps-warn',
    warnSoft: '--ps-warn-soft',
    danger: '--ps-danger',
    dangerSoft: '--ps-danger-soft',
    dangerLine: '--ps-danger-line',
    info: '--ps-info',
    infoSoft: '--ps-info-soft',
    scrim: '--ps-scrim',
    scrimHeavy: '--ps-scrim-heavy',
    tint: '--ps-tint',
    shadowSm: '--ps-shadow-sm',
    shadowMd: '--ps-shadow-md',
    shadowLg: '--ps-shadow-lg',
    shadowSheet: '--ps-shadow-sheet',
    shadowAccent: '--ps-shadow-accent',
};
/** `[组名, 键名] → CSS 变量名`。顶层字符串字段用 `''` 作组名。 */
export const SHAPE_VARS = {
    '': { font: '--ps-font', fontDisplay: '--ps-font-display', fontMono: '--ps-font-mono' },
    fontSize: {
        display: '--ps-fs-display',
        title: '--ps-fs-title',
        heading: '--ps-fs-heading',
        bodyLg: '--ps-fs-body-lg',
        body: '--ps-fs-body',
        label: '--ps-fs-label',
        caption: '--ps-fs-caption',
        micro: '--ps-fs-micro',
    },
    lineHeight: { tight: '--ps-lh-tight', snug: '--ps-lh-snug', body: '--ps-lh-body', loose: '--ps-lh-loose' },
    fontWeight: {
        regular: '--ps-fw-regular',
        medium: '--ps-fw-medium',
        semibold: '--ps-fw-semibold',
        bold: '--ps-fw-bold',
    },
    tracking: { tight: '--ps-tracking-tight', normal: '--ps-tracking-normal', wide: '--ps-tracking-wide' },
    spacing: {
        sp1: '--ps-sp-1',
        sp2: '--ps-sp-2',
        sp3: '--ps-sp-3',
        sp4: '--ps-sp-4',
        sp5: '--ps-sp-5',
        sp6: '--ps-sp-6',
        sp8: '--ps-sp-8',
        sp10: '--ps-sp-10',
        sp12: '--ps-sp-12',
        sp16: '--ps-sp-16',
        gutter: '--ps-gutter',
    },
    radius: {
        xs: '--ps-r-xs',
        sm: '--ps-r-sm',
        md: '--ps-r-md',
        lg: '--ps-r-lg',
        xl: '--ps-r-xl',
        sheet: '--ps-r-sheet',
        full: '--ps-r-full',
    },
    motion: {
        easeOut: '--ps-ease-out',
        easeInOut: '--ps-ease-in-out',
        easeSpring: '--ps-ease-spring',
        instant: '--ps-t-instant',
        fast: '--ps-t-fast',
        base: '--ps-t-base',
        slow: '--ps-t-slow',
    },
    zIndex: {
        sticky: '--ps-z-sticky',
        banner: '--ps-z-banner',
        tabbar: '--ps-z-tabbar',
        scrim: '--ps-z-scrim',
        sheet: '--ps-z-sheet',
        menu: '--ps-z-menu',
        dialog: '--ps-z-dialog',
        toast: '--ps-z-toast',
        viewer: '--ps-z-viewer',
        cover: '--ps-z-cover',
    },
    size: { hit: '--ps-hit', tabbarH: '--ps-tabbar-h', navbarH: '--ps-navbar-h', controlH: '--ps-control-h' },
};
/* ─────────────────────────── 默认皮肤 ───────────────────────────
 *
 * 取向：暖中性纸面 + 一个克制的强调色 + 高对比正文。
 * 为什么不是"随便一个蓝"：默认皮肤会被直接用在真实产品上，一个有性格但不喧宾夺主的
 * 中性系统比一个花哨的品牌色更耐用，也更容易被下游改成任何品牌色——只要改 accent 一组。
 */
const defaultLight = {
    canvas: '#f7f5f2',
    surface: '#fffdfa',
    surface2: '#ffffff',
    surfaceInput: '#fbf9f6',
    surfaceSunken: '#efece7',
    overlay: '#ffffff',
    line: '#e6e1d8',
    lineSoft: '#efebe4',
    lineStrong: '#d5cec2',
    text: '#1a1815',
    text2: '#6b6459',
    text3: '#948c7f',
    text4: '#b3aa9c',
    textInverse: '#fffdfa',
    accent: '#c2683e',
    accentHover: '#b25c34',
    accentActive: '#a4522c',
    accentSoft: '#f6e6dc',
    accentLine: '#e8cdbc',
    onAccent: '#ffffff',
    success: '#4f8f5f',
    successSoft: '#e8f1e9',
    warn: '#b07d1e',
    warnSoft: '#faf1de',
    danger: '#b33a2a',
    dangerSoft: '#fbeae6',
    dangerLine: '#eec8c0',
    info: '#4a6f96',
    infoSoft: '#e8eff6',
    scrim: 'rgb(24 20 16 / 45%)',
    scrimHeavy: 'rgb(12 10 8 / 78%)',
    tint: 'rgb(24 20 16 / 6%)',
    shadowSm: '0 1px 2px rgb(24 20 16 / 6%), 0 2px 8px -4px rgb(24 20 16 / 8%)',
    shadowMd: '0 4px 12px -4px rgb(24 20 16 / 10%), 0 12px 28px -16px rgb(24 20 16 / 18%)',
    shadowLg: '0 12px 32px -12px rgb(24 20 16 / 22%), 0 32px 60px -32px rgb(24 20 16 / 30%)',
    shadowSheet: '0 -8px 32px -12px rgb(24 20 16 / 20%)',
    shadowAccent: '0 8px 20px -10px rgb(194 104 62 / 55%)',
};
const defaultDark = {
    canvas: '#14120f',
    surface: '#1b1815',
    surface2: '#23201c',
    surfaceInput: '#201d19',
    surfaceSunken: '#100e0c',
    overlay: '#262320',
    line: '#332e28',
    lineSoft: '#2a2622',
    lineStrong: '#453e36',
    text: '#f2ede5',
    text2: '#a89f92',
    text3: '#837a6e',
    text4: '#5f584f',
    textInverse: '#14120f',
    // 深色底上强调色要提亮，否则读不出来
    accent: '#e08658',
    accentHover: '#e9946a',
    accentActive: '#cf7649',
    accentSoft: '#33241b',
    accentLine: '#4d3628',
    onAccent: '#1a1005',
    success: '#7fbc8c',
    successSoft: '#1c2a1f',
    warn: '#d9a94e',
    warnSoft: '#2c2415',
    danger: '#e2695a',
    dangerSoft: '#2e1a17',
    dangerLine: '#4a2620',
    info: '#7fa8d0',
    infoSoft: '#17222c',
    scrim: 'rgb(0 0 0 / 62%)',
    scrimHeavy: 'rgb(0 0 0 / 85%)',
    tint: 'rgb(255 255 255 / 7%)',
    // 深色里阴影几乎看不见，层级靠"面更亮"来表达；阴影只留一点点做形状暗示
    shadowSm: '0 1px 2px rgb(0 0 0 / 40%)',
    shadowMd: '0 6px 18px -8px rgb(0 0 0 / 60%)',
    shadowLg: '0 16px 40px -16px rgb(0 0 0 / 75%)',
    shadowSheet: '0 -8px 32px -12px rgb(0 0 0 / 60%)',
    shadowAccent: '0 8px 20px -10px rgb(224 134 88 / 35%)',
};
export const defaultShape = {
    /*
     * 系统字体栈是"原生感"最便宜也最有效的一环：用户设备上的字体渲染、字重、
     * 中文标点位置全都对，而任何 Web 字体在中文下都会有一处不对劲。
     * 品牌大标题可以另配 display 字体（自托管，零外链）。
     */
    font: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "PingFang SC", "HarmonyOS Sans SC", "Noto Sans SC", system-ui, "Segoe UI", Roboto, sans-serif',
    fontDisplay: 'var(--ps-font)',
    fontMono: 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    fontSize: {
        display: '34px',
        title: '24px',
        heading: '19px',
        bodyLg: '17px',
        body: '15.5px',
        label: '14px',
        caption: '13px',
        micro: '11.5px',
    },
    lineHeight: { tight: '1.2', snug: '1.35', body: '1.55', loose: '1.7' },
    fontWeight: { regular: '400', medium: '500', semibold: '600', bold: '700' },
    // 大标题收紧才不散；全大写小字要放开
    tracking: { tight: '-0.02em', normal: '0', wide: '0.02em' },
    spacing: {
        sp1: '4px',
        sp2: '8px',
        sp3: '12px',
        sp4: '16px',
        sp5: '20px',
        sp6: '24px',
        sp8: '32px',
        sp10: '40px',
        sp12: '48px',
        sp16: '64px',
        gutter: '16px',
    },
    radius: { xs: '6px', sm: '10px', md: '14px', lg: '18px', xl: '24px', sheet: '28px', full: '999px' },
    /*
     * 移动端的时长要短。>300ms 的转场在手机上就是"卡了一下"。
     * 缓动用"快出慢入"：手指离开后立刻响应，收尾慢一点才显得有质量。
     */
    motion: {
        easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)',
        easeInOut: 'cubic-bezier(0.65, 0, 0.35, 1)',
        easeSpring: 'cubic-bezier(0.34, 1.32, 0.64, 1)',
        instant: '90ms',
        fast: '160ms',
        base: '240ms',
        slow: '340ms',
    },
    zIndex: {
        sticky: '10',
        banner: '20',
        tabbar: '30',
        scrim: '100',
        sheet: '110',
        menu: '120',
        dialog: '130',
        toast: '200',
        viewer: '300',
        // 快照遮盖层必须在所有东西之上——它盖不住的那一块就是会被截进多任务卡片的那一块
        cover: '400',
    },
    size: { hit: '44px', tabbarH: '50px', navbarH: '44px', controlH: '46px' },
};
export const defaultSkin = {
    name: 'default',
    label: '默认',
    light: defaultLight,
    dark: defaultDark,
    shape: defaultShape,
};
const NAME_RE = /^[a-z][a-z0-9-]*$/i;
function mergeShape(base, patch) {
    if (!patch)
        return base;
    const out = { ...base };
    for (const [k, v] of Object.entries(patch)) {
        if (v === undefined)
            continue;
        const cur = out[k];
        out[k] = typeof v === 'object' && v !== null && typeof cur === 'object' && cur !== null ? { ...cur, ...v } : v;
    }
    return out;
}
/**
 * 定义一套皮肤。**只写要改的部分**，其余从 base（默认皮肤）继承。
 *
 * ```ts
 * export const ocean = defineSkin({
 *   name: 'ocean',
 *   label: '海',
 *   light: { accent: '#1d6f8b', accentHover: '#19607a', accentSoft: '#e2eff4', accentLine: '#bcd9e4' },
 *   dark:  { accent: '#63b6d1', accentHover: '#79c3da', accentSoft: '#122a33', accentLine: '#1d4453' },
 * })
 * ```
 *
 * ⚠️ **light 和 dark 都要给。** 只改浅色的话，用户切到深色就会看到上一套皮肤的强调色——
 * 而开发时如果一直在浅色下工作，这个问题要等到用户报障才会知道。
 * 真的只想跟随浅色，就显式把 dark 写成和 light 一样的值，让"我想清楚了"留在代码里。
 */
export function defineSkin(patch) {
    if (!NAME_RE.test(patch.name)) {
        throw new Error(`[pwaship] 皮肤名只能是字母开头的字母数字与连字符，收到 ${JSON.stringify(patch.name)}`);
    }
    const base = patch.base ?? defaultSkin;
    return {
        name: patch.name,
        label: patch.label ?? patch.name,
        light: { ...base.light, ...patch.light },
        dark: { ...base.dark, ...patch.dark },
        shape: mergeShape(base.shape, patch.shape),
    };
}
//# sourceMappingURL=tokens.js.map